import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../api/client";
import type { Coach, Gym } from "../../api/hooks";
import { PhotoField } from "../../components/PhotoField";
import { LoadingSection } from "../../components/Spinner";
import { RichTextField } from "../../components/admin/RichTextField";
import { stripHtml } from "../../components/RichText";

type CoachForm = Omit<Coach, "gym"> & { gymId?: string | null };

export function AdminCoaches() {
  const qc = useQueryClient();
  const { data: coaches, isLoading } = useQuery({
    queryKey: ["admin", "coaches"],
    queryFn: () => api<CoachForm[]>("/admin/coaches"),
  });
  const { data: gyms } = useQuery({
    queryKey: ["admin", "gyms"],
    queryFn: () => api<Gym[]>("/admin/gyms"),
  });
  const [editing, setEditing] = useState<CoachForm | null>(null);

  const save = useMutation({
    mutationFn: async (c: CoachForm) => {
      const body = {
        name: c.name,
        role: c.role,
        bio: c.bio,
        photoUrl: c.photoUrl,
        certifications: c.certifications,
        order: c.order,
        gymId: c.gymId,
      };
      if (c.id) return api(`/admin/coaches/${c.id}`, { method: "PUT", json: body });
      return api("/admin/coaches", { method: "POST", json: body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "coaches"] });
      qc.invalidateQueries({ queryKey: ["team"] });
      setEditing(null);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/admin/coaches/${id}`, { method: "DELETE" }),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["admin", "coaches"] });
      qc.invalidateQueries({ queryKey: ["team"] });
      if (editing?.id === id) setEditing(null);
    },
  });

  const handleDelete = (coach: CoachForm) => {
    const ok = window.confirm(
      `Supprimer le coach « ${coach.name} » ? Cette action est irréversible.`,
    );
    if (ok) remove.mutate(coach.id);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold uppercase">Coachs</h1>
        <button
          onClick={() =>
            setEditing({
              id: "",
              name: "",
              role: "",
              bio: "",
              certifications: [],
              order: 0,
              gymId: null,
              photoUrl: null,
            })
          }
          className="btn-primary"
        >
          + Ajouter
        </button>
      </div>

      {isLoading && <LoadingSection />}

      <div className="mt-6 grid gap-4 md:grid-cols-3">
        {coaches?.map((c) => (
          <div key={c.id} className="card">
            <h3 className="font-display text-lg font-bold">{c.name}</h3>
            <p className="text-sm text-brand-500">{c.role}</p>
            <p className="mt-2 text-xs text-ink-500 line-clamp-3">{stripHtml(c.bio)}</p>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setEditing(c)} className="btn-outline">Éditer</button>
              <button onClick={() => handleDelete(c)} className="btn-ghost text-brand-500">
                Suppr.
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-[80vw] overflow-y-auto rounded-lg bg-white p-6">
            <h2 className="font-display text-xl font-bold">
              {editing.id ? "Éditer" : "Nouveau coach"}
            </h2>
            <div className="mt-4 grid gap-3">
              <input
                className="input"
                placeholder="Nom"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
              <input
                className="input"
                placeholder="Rôle"
                value={editing.role}
                onChange={(e) => setEditing({ ...editing, role: e.target.value })}
              />
              <RichTextField
                label="Bio"
                placeholder="Présentation du coach…"
                value={editing.bio}
                onChange={(bio) => setEditing({ ...editing, bio })}
              />
              <PhotoField
                label="Photo du coach"
                value={editing.photoUrl ?? ""}
                onChange={(photoUrl) => setEditing({ ...editing, photoUrl })}
              />
              <input
                className="input"
                placeholder="Certifications (séparées par virgule)"
                value={editing.certifications.join(", ")}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    certifications: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                  })
                }
              />
              <select
                className="input"
                value={editing.gymId ?? ""}
                onChange={(e) => setEditing({ ...editing, gymId: e.target.value || null })}
              >
                <option value="">— Aucune salle —</option>
                {gyms?.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
              <input
                type="number"
                className="input"
                placeholder="Ordre"
                value={editing.order}
                onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })}
              />
            </div>
            <div className="mt-6 flex gap-2">
              <button onClick={() => save.mutate(editing)} className="btn-primary">
                Enregistrer
              </button>
              <button onClick={() => setEditing(null)} className="btn-ghost">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
